import { type ApolloError, useSubscription } from '@apollo/client'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { useUser } from '@/client/context/user'
import { useIsPageVisible } from '@/client/hooks'
import { USER_NOTIFICATIONS_SUBSCRIPTION } from '@/client/services/hasura'
import type {
  UserNotificationsSubscription,
  UserNotificationsSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'

type Data = {
  unreadNotificationCount: number
  notificationsLastViewedAt: DateTime | null
}

export default function useRealtimeUserNotificationDetails({ skip = false }: { skip?: boolean }): {
  loading: boolean
  error: ApolloError | null
  data: Data | null
} {
  const user = useUser()
  const isPageVisible = useIsPageVisible()

  const [data, setData] = useState<Data | null>(null)

  const { loading, error = null } = useSubscription<
    UserNotificationsSubscription,
    UserNotificationsSubscriptionVariables
  >(USER_NOTIFICATIONS_SUBSCRIPTION, {
    skip: !isPageVisible || skip,
    variables: {
      userId: user.id,
    },
    onData: newData => {
      const userFragment = newData.data?.data?.user_by_pk
      setData({
        unreadNotificationCount: userFragment?.unread_notification_count ?? 0,
        notificationsLastViewedAt:
          userFragment?.notifications_last_viewed_at == null
            ? null
            : DateTime.fromISO(userFragment.notifications_last_viewed_at),
      })
    },
  })

  return {
    loading,
    error,
    data,
  }
}
